import { customElement, property } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { fetch, updateArrayURLParameter } from "@/utilities";

const LOAD_DELAY_MS = 200;
const BATCH_WINDOW_MS = 50;
const RETRY_BASE_DELAY_MS = 2_000;
const RETRY_MAX_DELAY_MS = 60_000;

/**
 * Collects the loaders that become due within BATCH_WINDOW_MS and fetches
 * their cards with as few requests as the cards endpoint allows. All loaders
 * of a failed request get the same retry time, so they become due together
 * again. The backoff grows with the most failures any of them had, so a
 * request that succeeds does not reset the backoff of one that still fails.
 */
class SeriesCardBatch {
    private due: SeriesCardLoader[] = [];
    private timer: number | null = null;

    add(loader: SeriesCardLoader): void {
        this.due.push(loader);
        this.timer ??= window.setTimeout(() => this.flush(), BATCH_WINDOW_MS);
    }

    private flush(): void {
        const due = this.due;
        this.due = [];
        this.timer = null;
        const size = due[0].maxCards;
        for (let i = 0; i < due.length; i += size) {
            void this.load(due.slice(i, i + size));
        }
    }

    private async load(loaders: SeriesCardLoader[]): Promise<void> {
        const ids = loaders.map((loader) => loader.seriesId);
        try {
            const response = await fetch(updateArrayURLParameter(loaders[0].cardsUrl, "ids", ids), { method: "GET" });
            const js = await response.text();
            (0, eval)(js);
        } catch {
            const failures = Math.max(...loaders.map((loader) => loader.failures)) + 1;
            const backoff = Math.min(RETRY_BASE_DELAY_MS * 2 ** (failures - 1), RETRY_MAX_DELAY_MS);
            const retryAt = Date.now() + backoff * (0.5 + Math.random());
            loaders.forEach((loader) => loader.failed(retryAt, failures));
        }
    }
}

// Shared by the connected loaders, and dropped when the last one disconnects.
const loaders = new Map<Element, SeriesCardLoader>();
let observer: IntersectionObserver | null = null;
let batch: SeriesCardBatch | null = null;

// A scroll to a target clicked in the sidebar slows down near the end, so the
// cards it passes there would stay near the viewport long enough to load, one
// request each. No card becomes due during that scroll; when it lands, every
// card near the viewport becomes due at once.
let sidebarScrolling = false;
document.addEventListener("sidebar-scroll-start", () => {
    sidebarScrolling = true;
    loaders.forEach((loader) => loader.schedule());
});
document.addEventListener("sidebar-scroll-end", () => {
    sidebarScrolling = false;
    loaders.forEach((loader) => loader.schedule(0));
});

/**
 * Loads the lazy series card it sits in once that card has stayed near the
 * viewport for LOAD_DELAY_MS. A fast scroll, such as dragging the scrollbar,
 * passes the cards in between faster than that, so those cards are not
 * fetched. A card that is in view when its loader connects loads without that
 * delay, and so does one near the viewport when a sidebar scroll lands.
 *
 * The card is not observed while its request is in flight. A failed load
 * observes it again, and the observer then reports whether it is still visible.
 * The loaded card replaces the lazy one, which disconnects this element.
 *
 * Renders no template of its own.
 *
 * @element d-series-card-loader
 *
 * @prop {string} seriesId - Id of the series of the card.
 * @prop {string} cardsUrl - Endpoint that renders the cards whose ids it gets as ids[].
 * @prop {number} maxCards - Most ids the endpoint accepts in one request.
 */
@customElement("d-series-card-loader")
export class SeriesCardLoader extends DodonaElement {
    @property({ type: String, attribute: "series-id" })
    seriesId: string;

    @property({ type: String, attribute: "cards-url" })
    cardsUrl: string;

    @property({ type: Number, attribute: "max-cards" })
    maxCards: number;

    private card: HTMLElement;
    private reported = false;
    private visible = false;
    failures = 0;
    private retryAt = 0;
    private timer: number | null = null;

    connectedCallback(): void {
        super.connectedCallback();
        this.card = this.closest(".series.card");
        if (loaders.size === 0) {
            observer = new IntersectionObserver(
                (entries) => entries.forEach((entry) => loaders.get(entry.target)?.setVisible(entry.isIntersecting)),
                { rootMargin: "50% 0px" },
            );
            batch = new SeriesCardBatch();
        }
        loaders.set(this.card, this);
        observer.observe(this.card);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.clearTimeout(this.timer);
        observer.unobserve(this.card);
        loaders.delete(this.card);
        if (loaders.size === 0) {
            observer.disconnect();
            observer = null;
            batch = null;
        }
    }

    failed(retryAt: number, failures: number): void {
        this.retryAt = retryAt;
        this.failures = failures;
        if (this.isConnected) {
            observer.observe(this.card);
        }
    }

    schedule(minDelay = LOAD_DELAY_MS): void {
        window.clearTimeout(this.timer);
        this.timer = null;
        if (this.visible && !sidebarScrolling) {
            const delay = Math.max(minDelay, this.retryAt - Date.now());
            this.timer = window.setTimeout(() => {
                this.timer = null;
                this.visible = false;
                observer.unobserve(this.card);
                batch.add(this);
            }, delay);
        }
    }

    private setVisible(visible: boolean): void {
        const minDelay = this.reported ? LOAD_DELAY_MS : 0;
        this.reported = true;
        this.visible = visible;
        this.schedule(minDelay);
    }
}
